import { DateTime } from 'luxon';

/**
 * Formats an ISO date string to 'yyyy-MM-dd HH:mm:ss' format in UTC
 */
export const formatDateTime = (isoString) => {
    if (!isoString) return "";
    try {
        const dt = DateTime.fromISO(isoString, { zone: 'utc' });
        return dt.isValid ? dt.toFormat('yyyy-MM-dd HH:mm:ss') : "";
    } catch {
        return "";
    }
};

/**
 * Get unique values for a specific column from the data
 * Includes blank/null values as a single "Blanks" filter option
 */
export const getUniqueColumnValues = (data, columnId) => {
    if (!data || data.length === 0) return [];
    
    const values = [];
    let hasBlanks = false;
    
    data.forEach(item => {
        let value = item[columnId];
        // Handle boolean values
        if (typeof value === 'boolean') {
            values.push(value ? 'Yes' : 'No');
        } else if (value === null || value === undefined || value === '' || value === ' ') {
            hasBlanks = true;
        } else {
            values.push(String(value));
        }
    });
    
    // Get unique values and sort them
    const uniqueValues = [...new Set(values)].sort();
    
    // Add blanks option at the end if there are any blank/null values
    if (hasBlanks) uniqueValues.push('[Blanks]');
    
    return uniqueValues;
};

/**
 * Apply column header filters to the data
 * Handles regular values and blank/null values
 */
export const applyColumnFilters = (data, filters) => {
    if (Object.keys(filters).length === 0) return data;

    return data.filter(item => {
        return Object.entries(filters).every(([columnId, selectedValues]) => {
            // Ensure selectedValues is an array
            if (!Array.isArray(selectedValues) || selectedValues.length === 0) return true;
            
            let itemValue = item[columnId];
            let matchValue = '';
            
            // Handle null, undefined, empty string, and whitespace values
            if (itemValue === null || itemValue === undefined || itemValue === '' || itemValue === ' ') {
                matchValue = '[Blanks]';
            } else if (typeof itemValue === 'boolean') {
                // Handle boolean values
                matchValue = itemValue ? 'Yes' : 'No';
            } else {
                matchValue = String(itemValue);
            }
            
            return selectedValues.includes(matchValue);
        });
    });
};

/**
 * Applies global search across all incident fields
 */
export const applyGlobalSearch = (data, searchValue, searchType) => {
    if (!searchValue || searchType !== 'global') return data;

    // Split comma-separated search terms and clean them
    const terms = searchValue.split(',').map(term => term.trim().toLowerCase()).filter(term => term.length > 0);
    if (terms.length === 0) return data;
    
    return data.filter(item => {
        // Check if any search term matches any column value
        return terms.some(term => {
            return Object.values(item).some(value => {
                if (value === null || value === undefined) return false;
                return String(value).toLowerCase().includes(term);
            });
        });
    });
};

/**
 * Applies advanced filters to the data
 */
export const applyAdvancedFilters = (data, filters) => {
    if (filters.length === 0) return data;

    return data.filter(item => {
        return filters.every(filter => {
            // Map incidentId to id field in the data
            const fieldToCheck = filter.field === 'incidentId' ? 'id' : filter.field;
            let itemValue = item[fieldToCheck];
            
            // Handle boolean fields
            if (typeof itemValue === 'boolean') {
                itemValue = itemValue ? 'yes' : 'no';
            }
            
            itemValue = String(itemValue || '').toLowerCase();
            const filterValue = filter.value.toLowerCase();

            switch (filter.operator) {
                case 'equals':
                    return itemValue === filterValue;
                case 'contains':
                    return itemValue.includes(filterValue);
                case 'Does not contain':
                    return !itemValue.includes(filterValue);
                case 'startsWith':
                    return itemValue.startsWith(filterValue);
                case 'endsWith':
                    return itemValue.endsWith(filterValue);
                case 'Is not':
                    return itemValue !== filterValue;
                default:
                    return true;
            }
        });
    });
};

/**
 * Transform incident data for table display
 */
export const transformIncidentForDisplay = (incident, activeP1P2Tab) => {
    const baseData = {
        incidentNumber: incident.id || "",
        applicationId: incident.application_id || "",
        configurationItem: incident.configuration_item || "",
        shortDescription: incident.short_description || "",
        resolved: formatDateTime(incident.resolved),
        opened: formatDateTime(incident.opened),
        priority: incident.priority || "",
        brand: incident.brand || "",
        mvc: incident.mvc ? "Yes" : "No",
        platform: incident.platform || "",
        portfolio: incident.portfolio || "",
        causedByPlatform: incident.caused_by_platform || "",
        causedByPortfolio: incident.caused_by_portfolio || "",
        causedByChange: incident.caused_by_change === null ? "" : (incident.caused_by_change ? "Yes" : "No"),
    };

    // Add additional fields for 'P1/P2 due to change' tab
    if (activeP1P2Tab === "P1/P2 due to change") {
        // Convert triggering_change_request array to comma-separated string for display
        let triggeringChangeRequestValue = "";
        if (incident.triggering_change_request) {
            if (Array.isArray(incident.triggering_change_request)) {
                triggeringChangeRequestValue = incident.triggering_change_request.join(", ");
            } else {
                triggeringChangeRequestValue = incident.triggering_change_request;
            }
        }

        return {
            ...baseData,
            changeType: incident.change_type || "",
            changeProcess: incident.change_process || "",
            triggeringChangeRequest: triggeringChangeRequestValue,
            observations: incident.observations || "",
            rootCauseCode: incident.root_cause_code || "",
            rootCauseSubCode: incident.root_cause_sub_code || "",
        };
    }

    return baseData;
};

/**
 * Transform incident data for CSV export
 */
export const transformIncidentForCSV = (incident, activeP1P2Tab) => {
    const baseData = {
        "Incident Number": incident.id || "",
        "Configuration Item": incident.configuration_item || "",
        "Priority": incident.priority || "",
        "Short Description": incident.short_description || "",
        "Opened At": formatDateTime(incident.opened),
        "Resolved At": formatDateTime(incident.resolved),
        "Brand": incident.brand || "",
        "Platform": incident.platform || "",
        "Portfolio": incident.portfolio || "",
        "Application ID": incident.application_id || "",
        "MVC": incident.mvc ? "Yes" : "No",
        "Caused By Change": incident.caused_by_change ? "Yes" : "No",
        "Caused By Platform": incident.caused_by_platform || "",
        "Caused By Portfolio": incident.caused_by_portfolio || ""
    };

    // Add additional fields for 'P1/P2 due to change' tab
    if (activeP1P2Tab === "P1/P2 due to change") {
        return {
            ...baseData,
            "Change Type": incident.change_type || "",
            "Change Process": incident.change_process || "",
            "Triggering Change Request": incident.triggering_change_request || "",
            "Observations": incident.observations || "",
            "Root Cause Code": incident.root_cause_code || "",
            "Root Cause Sub Code": incident.root_cause_sub_code || ""
        };
    }

    return baseData;
};
